import classNames from "classnames";
import { FieldState } from "formstate";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { AgentProfile } from "@closerplatform/spinner-openapi";
import { AgentInput } from "@fidget/common/components/form/agentInput";
import {
  AutoCompleteProps,
  AutoComplete
} from "@fidget/common/components/form/autoComplete/autoComplete";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { useStore } from "../../../models";
import settingsStyles from "../../../styles/settings.css";
import { AdviserPickerItem } from "./adviserPickerItem";
import { filterAgentPredicate } from "../../settings/users/utils/utils";

interface Props extends Omit<Partial<AutoCompleteProps<AgentProfile>>, "onChange"> {
  agentId?: string;
  onChange(agent: AgentProfile): void;
  values?: AgentProfile[];
}

export const AdviserPicker = observer(
  ({ agentId, values, onChange, className, ...autoCompleteProps }: Props) => {
    const { agentStore } = useStore();
    const [inputField] = useState(new FieldState(""));

    const value = agentId ? agentStore.getById(agentId) : undefined;
    const agents: AgentProfile[] = values || agentStore.agentProfiles;

    const handleChange = (val: AgentProfile | "") => {
      if (val) {
        onChange(val);
      }
    };

    return (
      <AutoComplete<AgentProfile>
        GridItem={AdviserPickerItem}
        className={classNames(settingsStyles["input-max-width-bigger"], className)}
        onChange={handleChange}
        itemsProvider={
          inputField.$
            ? () => agents.filter((a) => agentInputPredicate(a, inputField.$))
            : () => agents
        }
        itemToString={(val) => (val && mapAgentToString(val)) || ""}
        placeholder={intl.formatMessage("adviserPicker.placeholder")}
        readOnly
        inputValue={value && mapAgentToString(value)}
        emptyPlaceholder={intl.formatMessage("adviserPicker.empty")}
        {...autoCompleteProps}
      >
        <AgentInput
          field={inputField}
          placeholder={intl.formatMessage("adviserPicker.input.placeholder")}
        />
      </AutoComplete>
    );
  }
);

const mapAgentToString = (agent: AgentProfile) => {
  return `${agent.firstName} ${agent.lastName}`;
};

const agentInputPredicate = (agent: AgentProfile, input: string): boolean =>
  filterAgentPredicate(input, agent.firstName, agent.lastName, agent.email || "");
